import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/context/I18nContext";
import type { Order, OrderStatus } from "@/lib/types";
import {
  Loader2,
  Search,
  ArrowUpDown,
  ShieldCheck,
  Clock,
  AlertCircle,
  Package,
  ChevronDown,
  ChevronUp,
  Truck,
  MapPin,
  FileText,
  RotateCcw,
  CreditCard,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { localizedDictionaryMeta } from "@/lib/utils/seo";
import { Button } from "@/components/ui/button";
import { useReorder } from "@/hooks/useReorder";

export const Route = createFileRoute("/account/orders")({
  head: () => ({
    meta: localizedDictionaryMeta(
      "account.orders.title",
      "account.orders.subtitle",
      "Orders & Quotes",
      "Track all of your B2B quotes and orders in one place.",
    ),
  }),
  component: Orders,
});

const statusVariant: Record<OrderStatus, { badge: string; icon: typeof Clock }> = {
  "Pending Review": { badge: "bg-amber-50 text-amber-700 border-amber-200/60", icon: Clock },
  Approved: { badge: "bg-emerald-50 text-emerald-700 border-emerald-200/60", icon: ShieldCheck },
  Shipped: { badge: "bg-sky-50 text-sky-700 border-sky-200/60", icon: Package },
  Cancelled: { badge: "bg-rose-50 text-rose-700 border-rose-200/60", icon: AlertCircle },
};

function Orders() {
  const { t } = useI18n();
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<"date" | "total">("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;
    async function loadOrders() {
      try {
        const res = await fetch("/api/portal/orders");
        if (res.ok && active) {
          const json = await res.json();
          setOrders(json.data || []);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadOrders();
    return () => {
      active = false;
    };
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSort = (field: "date" | "total") => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const filteredOrders = orders.filter((o) => {
    const query = searchQuery.toLowerCase();
    return (
      o.id.toLowerCase().includes(query) ||
      o.status.toLowerCase().includes(query) ||
      o.products.some((p) => p.toLowerCase().includes(query)) ||
      (o.lines && o.lines.some((l) => l.name.toLowerCase().includes(query)))
    );
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (sortField === "date") {
      const valA = new Date(a.date).getTime();
      const valB = new Date(b.date).getTime();
      return sortAsc ? valA - valB : valB - valA;
    } else {
      const valA = parseFloat(a.total?.replace(/[^0-9.]/g, "") || "0");
      const valB = parseFloat(b.total?.replace(/[^0-9.]/g, "") || "0");
      return sortAsc ? valA - valB : valB - valA;
    }
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-display text-3xl font-black text-navy">{t("account.orders.title")}</h1>
        <p className="mt-2 text-muted-foreground text-sm font-medium">
          {t("account.orders.subtitle")}
        </p>
      </div>

      {/* Control Panel */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
          <input
            type="text"
            placeholder={t("account.orders.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full ps-9 pe-4 py-2 text-sm rounded-xl border border-border bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-[30vh] items-center justify-center">
          <div className="text-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="text-sm font-semibold text-navy/80">{t("account.loadingOrders")}</p>
          </div>
        </div>
      ) : sortedOrders.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center border border-border/60 max-w-lg mx-auto space-y-4">
          <Package className="h-12 w-12 text-muted-foreground/60 mx-auto" />
          <div className="font-display text-xl font-bold text-navy">
            {t("account.orders.emptyTitle")}
          </div>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            {t("account.orders.emptyBody")}
          </p>
        </div>
      ) : (
        <div className="glass-card rounded-2xl border border-border/60 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm border-collapse text-start">
              <thead className="bg-surface-alt/50 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="w-10 px-4 py-4"></th>
                  <th className="px-6 py-4">{t("account.orders.col.quote")}</th>
                  <th
                    className="px-6 py-4 cursor-pointer hover:text-navy transition-colors select-none"
                    onClick={() => handleSort("date")}
                  >
                    <div className="flex items-center gap-1.5">
                      {t("account.orders.col.date")}
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="px-6 py-4">{t("account.orders.col.products")}</th>
                  <th
                    className="px-6 py-4 cursor-pointer hover:text-navy transition-colors select-none"
                    onClick={() => handleSort("total")}
                  >
                    <div className="flex items-center gap-1.5">
                      {t("account.orders.col.amount")}
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="px-6 py-4">{t("account.orders.col.status")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {sortedOrders.map((o) => {
                  const isExpanded = Boolean(expandedOrders[o.id]);
                  const statusInfo = statusVariant[o.status] || statusVariant["Pending Review"];
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tbody key={o.id} className="divide-y divide-border/40">
                      {/* Summary row */}
                      <tr
                        className={cn(
                          "hover:bg-muted/20 transition-colors cursor-pointer",
                          isExpanded && "bg-muted/15",
                        )}
                        onClick={() => toggleExpand(o.id)}
                      >
                        <td className="px-4 py-4.5 text-center text-muted-foreground/60">
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 mx-auto text-primary transition-transform" />
                          ) : (
                            <ChevronDown className="h-4 w-4 mx-auto text-muted-foreground/60 group-hover:text-primary transition-transform" />
                          )}
                        </td>
                        <td className="px-6 py-4.5 font-bold text-navy flex items-center gap-2">
                          <span>{o.id}</span>
                        </td>
                        <td className="px-6 py-4.5 text-muted-foreground font-semibold">
                          {o.date}
                        </td>
                        <td className="px-6 py-4.5 font-medium text-navy/90 max-w-[240px] truncate">
                          {o.products.join(", ")}
                        </td>
                        <td className="px-6 py-4.5 font-bold text-navy">{o.total ?? "—"}</td>
                        <td className="px-6 py-4.5">
                          <Badge
                            className={cn(
                              "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border shadow-none",
                              statusInfo.badge,
                            )}
                          >
                            <StatusIcon className="h-3.5 w-3.5 shrink-0" />
                            {o.status}
                          </Badge>
                        </td>
                      </tr>

                      {/* Expandable Order Detail Row */}
                      {isExpanded && (
                        <tr className="bg-surface-alt/30 border-b border-border/60">
                          <td colSpan={6} className="p-0">
                            <div className="p-6 space-y-6 animate-fade-in text-navy">
                              {/* Top Bar with Tracking / Incoterms */}
                              <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border/40 text-xs">
                                <div className="flex flex-wrap items-center gap-3">
                                  {o.trackingNumber && (
                                    <div className="flex items-center gap-1.5 font-semibold text-sky-800 bg-sky-50 border border-sky-200/60 px-3 py-1.5 rounded-lg">
                                      <Truck className="h-3.5 w-3.5 text-sky-600" />
                                      <span>
                                        {t("account.orders.detail.trackingNumber")}:{" "}
                                        {o.trackingNumber}
                                      </span>
                                    </div>
                                  )}
                                  {o.incoterm && (
                                    <div className="flex items-center gap-1.5 font-semibold text-navy/80 bg-white border border-border/60 px-3 py-1.5 rounded-lg shadow-2xs">
                                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span>
                                        {t("account.orders.detail.commercialTerms")}: {o.incoterm}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="text-muted-foreground text-xs font-medium">
                                  {t("account.orders.col.date")}: {o.date}
                                </div>
                              </div>

                              {/* Main Details Grid: Line Items & Summary */}
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Line Items Table (2 cols) */}
                                <div className="lg:col-span-2 space-y-3">
                                  <h4 className="font-display text-sm font-bold tracking-tight text-navy flex items-center gap-2">
                                    <Package className="h-4 w-4 text-primary" />
                                    {t("account.orders.detail.lineItems")}
                                  </h4>

                                  {o.lines && o.lines.length > 0 ? (
                                    <div className="rounded-xl border border-border/60 overflow-hidden bg-white shadow-2xs">
                                      <table className="w-full text-xs text-start">
                                        <thead className="bg-surface-alt/60 border-b border-border/40 text-muted-foreground font-bold uppercase">
                                          <tr>
                                            <th className="px-4 py-2.5 text-start">
                                              {t("account.orders.detail.item")}
                                            </th>
                                            <th className="px-4 py-2.5 text-center">
                                              {t("account.orders.detail.qty")}
                                            </th>
                                            <th className="px-4 py-2.5 text-end">
                                              {t("account.orders.detail.unitPrice")}
                                            </th>
                                            <th className="px-4 py-2.5 text-end">
                                              {t("account.orders.detail.subtotal")}
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/30">
                                          {o.lines.map((line, idx) => (
                                            <tr
                                              key={line.id || idx}
                                              className="hover:bg-muted/10 transition-colors"
                                            >
                                              <td className="px-4 py-3 font-semibold text-navy">
                                                {line.name}
                                              </td>
                                              <td className="px-4 py-3 text-center text-muted-foreground font-medium">
                                                {line.quantity}
                                              </td>
                                              <td className="px-4 py-3 text-end text-muted-foreground font-mono">
                                                {line.unitPrice ?? "—"}
                                              </td>
                                              <td className="px-4 py-3 text-end font-bold text-navy font-mono">
                                                {line.priceSubtotal ?? "—"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="rounded-xl border border-border/60 bg-white p-4 text-xs text-muted-foreground">
                                      {t("account.orders.detail.noLineItems")}
                                    </div>
                                  )}
                                </div>

                                {/* Summary & Shipping Cards (1 col) */}
                                <div className="space-y-4">
                                  {/* Pricing Breakdown */}
                                  <div className="rounded-xl border border-border/60 bg-white p-4 space-y-3 shadow-2xs text-xs">
                                    <div className="font-bold text-navy text-sm border-b border-border/40 pb-2">
                                      {t("account.orders.col.amount")}
                                    </div>
                                    <div className="space-y-2">
                                      {o.amountUntaxed && (
                                        <div className="flex justify-between text-muted-foreground">
                                          <span>{t("account.orders.detail.subtotal")}</span>
                                          <span className="font-mono font-medium">
                                            {o.amountUntaxed}
                                          </span>
                                        </div>
                                      )}
                                      {o.amountTax && (
                                        <div className="flex justify-between text-muted-foreground">
                                          <span>{t("account.orders.detail.tax")}</span>
                                          <span className="font-mono font-medium">
                                            {o.amountTax}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex justify-between font-bold text-sm text-navy pt-2 border-t border-border/40">
                                        <span>{t("account.orders.col.amount")}</span>
                                        <span className="font-mono text-primary font-black">
                                          {o.total}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Shipping Address Card */}
                                  {o.shippingAddress && (
                                    <div className="rounded-xl border border-border/60 bg-white p-4 space-y-2 shadow-2xs text-xs">
                                      <div className="font-bold text-navy text-sm flex items-center gap-1.5 border-b border-border/40 pb-2">
                                        <MapPin className="h-3.5 w-3.5 text-primary" />
                                        {t("account.orders.detail.shippingAddress")}
                                      </div>
                                      <div className="text-muted-foreground leading-relaxed">
                                        {o.shippingAddress.name && (
                                          <div className="font-semibold text-navy">
                                            {o.shippingAddress.name}
                                          </div>
                                        )}
                                        {o.shippingAddress.street && (
                                          <div>{o.shippingAddress.street}</div>
                                        )}
                                        {o.shippingAddress.city && (
                                          <div>
                                            {o.shippingAddress.city}
                                            {o.shippingAddress.country
                                              ? `, ${o.shippingAddress.country}`
                                              : ""}
                                          </div>
                                        )}
                                        {o.shippingAddress.phone && (
                                          <div className="pt-1 text-navy/80 font-mono">
                                            {o.shippingAddress.phone}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <OrderActions order={o} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * What a buyer can still do with a past order.
 *
 * Its own component because it holds reorder state: one hook instance per
 * order row, so a reorder running on one order cannot show its result under
 * another. Renders nothing at all when neither action applies — an order with
 * no re-buyable line and nothing left to pay should not grow an empty toolbar.
 */
function OrderActions({ order }: { order: Order }) {
  const { t } = useI18n();
  const { reorder, busy, result } = useReorder();
  const reorderable = (order.lines ?? []).some((line) => line.productSlug);

  if (!reorderable && !order.paymentUrl) return null;

  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border/40 pt-4">
      {order.paymentUrl && (
        <Button asChild size="sm" className="rounded-xl text-xs font-bold">
          {/* Odoo's own portal page, on our Odoo origin (the BFF drops any
              other), where the payment providers and the whole pay/redirect/
              webhook cycle live. Deliberately a full navigation, not a fetch. */}
          <a href={order.paymentUrl} rel="noopener">
            <CreditCard className="me-1.5 h-3.5 w-3.5" />
            {t("account.orders.actions.payNow")}
          </a>
        </Button>
      )}

      {reorderable && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => reorder(order.lines)}
          className="rounded-xl text-xs font-bold"
        >
          {busy ? (
            <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="me-1.5 h-3.5 w-3.5" />
          )}
          {t("account.orders.actions.reorder")}
        </Button>
      )}

      {result && (
        <p aria-live="polite" className="text-xs font-semibold text-muted-foreground">
          {result.added > 0 && (
            <span className="text-mint-ink">
              {t("account.orders.actions.reorderAdded").replace("{count}", String(result.added))}
            </span>
          )}
          {result.added > 0 && result.skipped > 0 && " · "}
          {/* Named rather than silent: on this store the usual reason is that
              the product has no price in Odoo, and a buyer who is not told
              will read the short cart as a bug. */}
          {result.skipped > 0 && (
            <span>
              {t("account.orders.actions.reorderSkipped").replace(
                "{count}",
                String(result.skipped),
              )}
            </span>
          )}
        </p>
      )}
    </div>
  );
}
